import { canUseDOM } from '../executionEnvironment';
import {
  ACADEMY,
  GIFT_CARD_OFFER_TYPES,
  MYACCOUNT_PATHS_SPECIAL_ANALYTICS_HANDLING,
  NOT_SET,
  PAGE_HIERARCHY_MAP,
  PHONE_NUMBER_KEY_TO_REMAP,
  REMAPPED_PHONE_NUMBER_KEY,
  REMAPPED_ZIP_CODE_KEY,
  TEALIUM_SCRIPT_LOADED,
  ZIP_CODE_KEY_TO_REMAP,
} from './constants';

/**
 * @param {any} variable a variable of any type
 * @returns sanitized variable
 */
export const sanitizeVariable = variable => {
  switch (typeof variable) {
    case 'boolean':
    case 'number':
      return `${+variable}`;
    case 'string':
      return variable.replace(/['"]/g, '\\$&');
    default:
      return NOT_SET;
  }
};

/**
 * Gets the sanitized version of a Tealium data object
 * Currently being used in {@link trackViewEvent} and {@link trackLinkEvent}
 * By default, assigns an empty object to the data object,
 * an empty array to an array data variable, and NOT_SET to a primitive data variable
 *
 * @param {object} dataObject - data object to be sanitized for Tealium tracking
 * @return sanitized data object
 */
export const getSanitizedTealiumDataObject = dataObject =>
  dataObject
    ? Object.entries(dataObject).reduce((accumulator, current) => {
        const [key, value] = current;
        accumulator[key] = Array.isArray(value) ? value.map(subValue => sanitizeVariable(subValue)) : sanitizeVariable(value);
        return accumulator;
      }, {})
    : {};

/**
 * An SSR-safe wrapper around calling utag.link().
 * Your data object should include any page or user metadata.
 *
 * @param {object} dataObject - complete data object to be passed to tealium utag.link().
 * @return true if this called successfully, or false for SSR mode, no utag available, or exceptions caught.
 * @see {@link getPageMetadata}
 */
export const trackLinkEvent = dataObject => {
  if (canUseDOM && window.utag) {
    try {
      window.utag.link(getSanitizedTealiumDataObject(dataObject));
      return true;
    } catch (err) {
      console.error('Error sending link data to utag:', err);
    }
  }
  return false;
};

/**
 * An SSR-safe wrapper around calling utag.view().
 * Your data object should include any page or user metadata.
 *
 * NOTE: in default operation, loading the utag.js script will auto-fire a utag "view" event as well.
 * If this is not desired, see 'deferUtagViewOnLoad' in the oden project and configure your page
 * configurations to set this to true. See also 'window.utag_cfg_ovrd.noview'.
 *
 * @param {object} dataObject - complete data object to be passed to tealium utag.view().
 * @return true if this called successfully, or false for SSR mode, no utag available, or exceptions caught.
 * @see {@link getPageMetadata}
 */
export const trackViewEvent = dataObject => {
  if (canUseDOM && window.utag) {
    try {
      window.utag.view(getSanitizedTealiumDataObject(dataObject));
      return true;
    } catch (err) {
      console.error('Error sending view data to utag:', err);
    }
  }
  return false;
};

/**
 * Returns whether tealium utag.js script has completely loaded and initialized, by safely checking at
 * window.utag. (Provided for convenience.)
 * @returns true of window.utag is defined, or false otherwise
 */
export const isUtagLoaded = () => canUseDOM && !!window?.utag?.id; // utag.id is defined when utag.js executes. be sure the oden 'event queue' helper does not also define one.

/**
 * Load the tealium utag script for given environment, if it is not yet loaded. note that
 * any definitions in window.utag_data must all be in place before calling this. The helper
 * "utag_load" function is defined in oden, in a head/script IIFE block.
 *
 * @param (function) onLoadComplete - optional callback function called after browser successfully loads utag.js
 * @return true if this called successfully, or false if utag was already loaded
 * (or is called from SSR, or if any exception was caught).
 */
export const loadUtagScript = onLoadComplete => {
  if (canUseDOM && !isUtagLoaded() && window?.aso?.utag_load) {
    try {
      window.aso.utag_load(onLoadComplete);
      return true;
    } catch (err) {
      console.error('Error calling utag load helper:', err);
    }
  }
  return false;
};

/**
 * An SSR-safe wrapper around dispatching action to set tealium loaded state in store.
 */
export const onLoaded = () =>
  canUseDOM &&
  window.store &&
  window.store.dispatch({
    type: TEALIUM_SCRIPT_LOADED,
    payload: true,
  });

/**
 * Returns value of our SSR-generated "utag_auto_load" setting, which directs the automatic load/exeuction of the tealium utag.js file. (Provided for convenience.)
 * @returns true if value of window.aso.utag_auto_load is truthy, or false otherwise
 */
export const isUtagAutoLoadEnabled = () => canUseDOM && !!window?.aso?.utag_auto_load;

/**
 * Merge a specified data object into any existing tealium utag_data object
 * on the window object. This can be called multiple times if needed, but must be called
 * before the utag.js script is loaded to have any effect.
 *
 * Note various pages/page types will, by default, load the utag script (see vendors.js)
 * This "utag_auto_load" can be controlled upstream in the oden (SSR) environment. Look for
 * 'deferUtagScriptLoad' (and somewhat related, 'deferUtagViewOnLoad')
 *
 * @param (object) dataObject - a simple object to be merged on to any existing window.utag_data object.
 * @return none / undefined
 */
export const appendUtagData = dataObject => {
  /* istanbul ignore else */
  if (canUseDOM) {
    window.utag_data = { ...(window.utag_data || {}), ...(dataObject || {}) };
  }
};

/**
 * Gets a simple object with common analytics fields about the current page.
 * Note some few events may also want pageHierarchy included.
 *
 * @returns object with common fields (pageType, pageName, pageEnv), any of which may have the 'not set'
 * placeholder value.
 * @see {@link getPageHierarchy}
 */
export const getPageMetadata = (isPageCategory = false) => {
  const { utag_data: { pageType = NOT_SET, pageName = NOT_SET, pageEnv = NOT_SET, pageCategory = NOT_SET } = {} } = window;
  const pageCatParameter = isPageCategory ? { pageCategory } : null;
  return {
    pageType,
    pageName,
    pageEnv,
    ...pageCatParameter,
  };
};

/**
 * Get a pageTitle with common analytics fields about the current page.
 * @returns string with common field pageTitle any of which may have the 'not set' placeholder value.
 */
export const getPageTitle = () => {
  const { utag_data: { pageTitle = NOT_SET } = {} } = window;
  return pageTitle;
};

/**
 * @returns boolean indicating whether deferred view is activated
 */
export const isDeferredView = () => !!window?.utag_cfg_ovrd?.noview;

/**
 * Generates an object with Tealium categories based on the provided category.
 * Impacted pages Cart/Checkout/OC
 * @param {string} category - The category to use for all levels (L1, L2, L3, L4).
 * @returns {object} An object with Tealium categories.
 */

export const getTealiumCategories = category => ({ L1category: category, L2category: category, L3category: category, L4category: category });

/**
 * Takes an object and removes key/value pairs for which the value is falsy as well as with the following keys renamed:
 * 1. 'zipCode' to 'zip'
 * 2. 'phoneNumber' to 'phone'
 * @param {object} object
 * @returns {object}
 */
export const frameUserProfileObj = object =>
  typeof object === 'object' && object !== null
    ? Object.entries(object).reduce((accumulator, current) => {
        const [key, value] = current;
        if (value) {
          switch (key) {
            case ZIP_CODE_KEY_TO_REMAP:
              accumulator[REMAPPED_ZIP_CODE_KEY] = value;
              break;
            case PHONE_NUMBER_KEY_TO_REMAP:
              accumulator[REMAPPED_PHONE_NUMBER_KEY] = value;
              break;
            default:
              accumulator[key] = value;
              break;
          }
        }
        return accumulator;
      }, {})
    : {};

/**
 * @description Convert the string to lowercase and all special characters to underscore. Also replace consecutive underscores with a single underscore.
 * @param  {string} string
 * @returns {string} converted string or empty string if input was invalid.
 */
export const applyLowerCaseAndSpecialCharRule = string =>
  typeof string === 'string'
    ? string
        .trim()
        .toLowerCase()
        .replace(/([^\w]+|\s+$)/g, '_')
        .replace(/_+/g, '_')
    : '';

/**
 * Checks for the type of the provided variable and applies lower case and special character rules.
 * Strings and arrays of strings are converted based on the lowercase and special character rules.
 * Empty strings are converted to not set. Data types other than strings and arrays are converted to not set.
 * @param {any} variable
 * @returns {string|array} returns a string or an array of strings.
 */
export const transformationMiddleware = variable => {
  switch (typeof variable) {
    case 'string':
      return applyLowerCaseAndSpecialCharRule(variable) || NOT_SET;
    case 'object':
      return Array.isArray(variable) ? variable.map(element => applyLowerCaseAndSpecialCharRule(element) || NOT_SET) : NOT_SET;
    default:
      return NOT_SET;
  }
};

/**
 * (SSR safe)
 * extracts the L-category data from utag data on the window object.
 * Checks for the presence of an L4 category, if undefined (not available) return empty object.
 * @returns the L-Category data form the utag data or an empty object.
 */
export const getPageLCategoryData = () => {
  /* istanbul ignore else : safety net that never actually gets executed in JSDOM */
  if (canUseDOM) {
    const { utag_data: { L1category, L2category, L3category, L4category } = {} } = window;
    return L4category ? { L1category, L2category, L3category, L4category } : {};
  }
  /* istanbul ignore next : safety net that never actually gets executed in JSDOM */
  return {};
};

/**
 * Gets initial value of pageHierarchy from window.utag_data, if it exists. Note various pages or sub-views
 * (in routed SPA's) may have more complex requirements for their pageHierarchy values, and may need to provide
 * their own value in any tracked 'view' or 'link' events.
 *
 * @returns string value of 'pageHierarchy' from utag_data, or the 'not set' placeholder value.
 */
export const getPageHierarchy = path => {
  let { utag_data: { pageHierarchy = NOT_SET } = {} } = window;

  if (!path) {
    return pageHierarchy;
  }

  pageHierarchy = path.replace('/', '').replace(/-/g, ' ').replace(/\//g, ' > ');

  MYACCOUNT_PATHS_SPECIAL_ANALYTICS_HANDLING.forEach(myAccountPath => {
    if (path.includes(myAccountPath)) {
      pageHierarchy = PAGE_HIERARCHY_MAP[myAccountPath];
    }
  });

  return pageHierarchy ? `${ACADEMY} > ${pageHierarchy}` : ACADEMY;
};

/**
 * Checks and returns the gift card offer type based on product info.
 * @param {object} productItem
 * @returns {string}
 */
export const getGiftCardOfferType = productItem => {
  if (productItem?.isEGCItem === 'Y') return GIFT_CARD_OFFER_TYPES.E_GIFT_CARD;
  if (productItem?.skuDetails?.skuInfo?.isGiftCard === 'Y') return GIFT_CARD_OFFER_TYPES.PYHSICAL_GIFT_CARD;
  return GIFT_CARD_OFFER_TYPES.FREE_GIFT;
};

/**
 * Get a pageCategory with common analytics fields about the current page.
 * @returns string with common field pageCategory any of which may have the 'not set' placeholder value.
 */
export const getPageCategory = customCategory => {
  /* istanbul ignore else : safety net that never actually gets executed in JSDOM */
  if (canUseDOM) {
    if (customCategory) return `${customCategory}`;
    const { utag_data = {} } = window;
    return utag_data.pageCategory || utag_data.pageType || NOT_SET;
  }
  /* istanbul ignore next : safety net that never actually gets executed in JSDOM */
  return NOT_SET;
};

/**
 * IF THIS IS EVER UNCOMMENTED, TESTS SHOULD BE ADDED
 *
 * Similar to trackViewEvent also providing the ability to wait for the action to complete.
 * Primary use case would be tracking an event right before page navigation and ensuring it gets recorded.
 * @param {*} dataObject to be written to tealium framework
 * @returns a promise that resolves when either the tealium tracking completes, or a timeout of 500 ms.
 * If you want to work with return values:
 * * true = tealium event suceeded before timout
 * * false = timout ocurred first
 * If you want to work with the promise framework
 * * resolve (then) = tealium event succeeded before timout
 * * reject (catch) = timeout ocurred first
 * @see trackViewEvent
 */
// export const trackViewEventAwait = dataObject => {
//   const tealium = new Promise(resolve => {
//     if (canUseDOM && window.utag) {
//       try {
//         window.utag.view(getSanitizedTealiumDataObject(dataObject), resolve(true));
//       } catch (err) {
//         console.error('Error sending view data to utag:', err);
//       }
//     }
//   });
//   let timer;
//   const timeout = new Promise((_resolve, reject) => {
//     // eslint-disable-next-line prefer-promise-reject-errors
//     timer = setTimeout(() => reject(false), 500);
//   });
//   return Promise.race([tealium, timeout]).finally(() => clearTimeout(timer));
// };

/**
 * IF THIS IS EVER UNCOMMENTED, ALSO UNCOMMENT THE ASSOCIATED TESTS
 *
 * Attach a script 'load' listener for an in-flight utag.js script load operation. This function is provided for unusual
 * corner cases where some external code has (already) triggered utag.js loading but you still want some kind of callback
 * to know when that in-flight operation completes.
 *
 * @param {function} onLoadComplete - callback function executed after utag.js loads/starts
 * @param {boolean} fireIfAlreadyLoaded - whether to call the callback function if utag.js is already loaded. default=false.
 */
// export const addUtagLoadListener = (onLoadComplete, fireIfAlreadyLoaded = false) => {
//   if (canUseDOM && onLoadComplete) {
//     if (isUtagLoaded()) {
//       if (fireIfAlreadyLoaded) {
//         onLoadComplete();
//       }
//     } else {
//       // not yet loaded...
//       const scriptTag = document.getElementById('script-utag-js'); // see oden project
//       if (scriptTag) {
//         // ...and script tag defined, so likely pending browser execution:
//         scriptTag.addEventListener('load', onLoadComplete);
//       }
//     }
//   }
// };
